<template>
  <div class="main-dashboard-panel">
    <div class="panel-topnav container-fluid">
      <router-link
        :to="{ name: 'dashboardApplicationList' }"
        class="back-link pull-left"
      >
        <i class="ion-ios-arrow-back text-yellow before"></i> My applications
      </router-link>

      <ul class="nav nav-tabs">
        <li>
          <h3 class="page-title">
            Application to: {{ application.organization.name }}
          </h3>
        </li>
      </ul>
      <div class="clearfix"></div>
    </div>

    <div v-loadcover="fetchInProgress" class="container-fluid pane">
      <ProgramForm
        v-show="application.program.type === 'FORM' || isProfileComplete"
        ref="programForm"
        :description="application.description"
        :questions="application.questions"
        :show-invalid-state="showInvalidState"
        :prefilled-questions="prefilledQuestions"
        @change="setFormData"
      />

      <div
        v-if="application.program.type !== 'FORM'"
        class="program-form margin-top-30"
      >
        <p v-if="application.program.type === 'PROFILE'" class="description">
          To apply to this program you need to fill in your
          <router-link :to="{ name: 'dashboard' }">
            company profile </router-link
          >. Submit the application when it's ready.
        </p>
        <p v-if="application.program.type === 'BOTH'" class="description">
          Please note that this application should include your
          <router-link :to="{ name: 'dashboard' }">
            company profile </router-link
          >. Make sure to fill it in with relevant information.
        </p>
        <p v-if="!isProfileComplete" class="description">
          There are {{ application.questions.length }} questions waiting but you
          must complete your company's profile first. At least
          {{ profileThreshold }}% completness is required in order to unlock
          whole application.
        </p>
      </div>

      <div v-if="application.program.type !== 'FORM'" class="program-form">
        <div class="row">
          <div class="col-sm-12 col-lg-8">
            <el-progress
              :percentage="completionReport.progress"
              :status="completionReport.progressStatus"
            />
            <a
              v-show="!reportVisible"
              class="text-sm"
              href
              @click.prevent="reportVisible = true"
              >Show details</a
            >
            <a
              v-show="reportVisible"
              class="text-sm"
              href
              @click.prevent="reportVisible = false"
              >Hide details</a
            >
            <ProfileCompletionReport
              v-show="reportVisible"
              :startup="startup"
            />

            <div
              v-if="showInvalidState && profileNeedsToBeCompleted"
              class="program-form-field"
            >
              <p class="validation-text text-danger">
                Your application is not complete. Please fill in the
                <router-link :to="{ name: 'dashboard' }">
                  PROFILE SECTION
                </router-link>
                as well (at least {{ profileThreshold }}% is required).
              </p>
            </div>
          </div>
        </div>
      </div>

      <hr class="margin-top-30" />

      <!-- TODO: put this within element below -->
      <!-- v-if="application.state === DRAFT" -->
      <el-button
        class="margin-bottom-20"
        type="primary"
        size="mini"
        :disabled="submitInProgress || profileNeedsToBeCompleted"
        :loading="submitInProgress"
        @click.prevent="submit()"
      >
        Submit
      </el-button>

      <Fader ref="saveIndicator" class="inline-block">
        <span class="text-success">Saved</span>
      </Fader>

      <span v-if="application.state === SUBMITTED" class="text-muted text-sm"
        >Form has been already submitted at
        {{ application.submitted_at | date }}
      </span>

      <span v-if="profileNeedsToBeCompleted" class="text-muted text-sm">
        Profile must be completed before application can be submitted Please
        click "show details" above to learn more.
      </span>

      <!-- <span class="text-muted">
        Finished {{ answeredQuestionsCount}} out of {{ questionsCount }}
      </span> -->
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ProgramForm from '@/modules/dashboard-organizer/ProgramForm'
import http from '@/services/http'
import ProfileCompletionReport from 'components/startups/ProfileCompletionReport.vue'
import { getReport } from 'services/startup-report'
import _ from 'lodash'
import { htmlToText } from '@/utils/forms'
import { toLegacyQuestionMap } from '@/services/faq-initial-sections'
import Fader from 'components/Fader.vue'
import * as Sentry from '@sentry/vue'

const DRAFT = 'draft'
const SUBMITTED = 'submitted'

export default {
  components: { Fader, ProgramForm, ProfileCompletionReport },
  computed: {
    ...mapState({
      application: (state) => state.startupApplicationDetails.application,
      applicationFetchPromise: (state) =>
        state.startupApplicationDetails.fetchPromise,
      startup: (state) => state.startupProfile.startup,
      startupFetched: (state) => state.startupProfile.fetchDone,
      fetchInProgress: (state) =>
        state.startupApplicationDetails.fetchInProgress,
      submitInProgress: (state) =>
        state.startupApplicationDetails.submitInProgress,
    }),
    profileThreshold: () => 80,
    completionReport() {
      return getReport(this.startup, this.profileThreshold)
    },
    isProfileComplete() {
      return this.completionReport.progress >= this.profileThreshold
    },
    profileNeedsToBeCompleted() {
      return this.application.program.type !== 'FORM' && !this.isProfileComplete
    },
    areStartupAndApplicationFetched() {
      return this.application.id && this.startupFetched
    },
  },
  watch: {
    areStartupAndApplicationFetched: {
      immediate: true,
      async handler(fetched) {
        if (this.didRunPrefillQuestions) {
          return
        }
        if (!fetched) {
          return
        }
        this.didRunPrefillQuestions = true
        await this.applicationFetchPromise
        this.fillAnswersFromProfile()
      },
    },
  },
  data() {
    return {
      DRAFT,
      SUBMITTED,
      showInvalidState: false,
      reportVisible: false,
      initialFetchDone: false,
      canSave: false,
      prefilledQuestions: {},
      didRunPrefillQuestions: false,
    }
  },
  beforeDestroy() {
    this.$store.commit('startupApplicationDetails/CLEAR')
  },
  async created() {
    await this.fetchApplication()
    this.initialFetchDone = true
    // prevent save for a moment just after component is shown
    await http.sleep(250)
    this.canSave = true
  },
  async mounted() {
    const application = await this.fetchApplication()
    if (_.toPairs(application.answers).length) {
      this.$nextTick(() => {
        this.$refs.programForm.setAnswers(application.answers)
      })
    }
  },
  methods: {
    setFormData(data, field) {
      this.formData = data
      this.$store.commit(
        'startupApplicationDetails/SET_ANSWERS',
        data.map((field) => {
          return {
            value: field.value,
            selected: field.selected,
          }
        }),
      )
      if (this.canSave) {
        this.debouncedSaveField(field)
      }
    },
    debouncedSaveField: _.debounce(async function (field) {
      const params = {
        ...(await this.getParams()),
        data: {
          organization_id: this.application.organization.id,
          question_id: field.question.id,
          answer: field.answer,
        },
      }

      if (isFieldAnswerEmpty(field.answer)) {
        await this.$store.dispatch(
          `startupApplicationDetails/clearAnswer`,
          params,
        )
        this.$refs.saveIndicator.show()
        return
      }
      if (!field.isValid) {
        return
      }
      await this.$store.dispatch(`startupApplicationDetails/saveAnswer`, params)
      this.$refs.saveIndicator.show()
    }, 500),
    async submit() {
      return this.performSave('submit')
    },
    async performSave(type = 'submit') {
      this.updateFaq()
      this.showInvalidState = true
      if (!this.$refs.programForm.isValid || this.profileNeedsToBeCompleted) {
        const title = 'Failed'
        let message = 'Please answer to all questions'
        if (this.profileNeedsToBeCompleted) {
          message += ' and/or fill company profile'
        }
        this.$notify({ title, message, type: 'error' })
        return
      }
      const requestData = await this.getParamsWithData({ delay: 1000 })
      const url = `startupApplicationDetails/${type}`
      try {
        const applicationSavePromise = this.$store.dispatch(url, requestData)
        const profileSavePromise = this.$store.dispatch(
          'startupProfile/saveChanges',
          {
            fields: 'faq',
          },
        )
        await Promise.all([applicationSavePromise, profileSavePromise])
        this.$notify({
          title: 'Application sent successfully',
          type: 'success',
        })
        this.fetchApplication()
      } catch (error) {
        this.$notify({ title: 'Form submission failed', type: 'error' })
        const extra = { url, requestData }
        if (error.response) {
          extra.response = error.response
        }
        Sentry.captureException(error, {
          level: 'warning',
          extra,
        })
        if (!error.response) {
          throw error
        }
      }
    },

    getAnswers() {
      if (!this.formData) {
        this.formData = this.$refs.programForm.getFormData()
      }
      const answers = this.formData.map((a) => {
        a.profile_attr = a.question ? a.question.profile_attr : null
        return a
      })
      return _.fromPairs(answers.map((answer) => [answer.question.id, answer]))
    },

    async getParams() {
      const user = await this.$store.dispatch('user/getUser')
      const startupId = user.startupId
      const applicationId = this.$route.params.id
      return { startupId, applicationId }
    },
    async getParamsWithData(extraParams) {
      extraParams = extraParams || {}
      const params = await this.getParams()
      const answers = this.getAnswers()
      params.data = {
        organization_id: this.application.organization.id,
        answers,
      }
      return { ...params, ...extraParams }
    },
    async fetchApplication() {
      const params = await this.getParams()
      await this.$store.dispatch('startupApplicationDetails/fetch', params)
      return this.$store.state.startupApplicationDetails.application
    },
    fillAnswersFromProfile() {
      // should be called only after startup and application are both fetched

      // gather faq answers from startup and if they are not answered yet,
      // prefill them at the application
      // this way it would be filled "each time this component is initially rendered",
      // meaning it is not stored at the backend yet and can be changed

      this.application.questions.forEach((question) => {
        if (question.profile_attr) {
          const answer = getAnswerFromProfile({
            startup: this.startup,
            attr: question.profile_attr,
            question,
          })
          if (!isFieldAnswerEmpty(answer)) {
            this.$set(this.prefilledQuestions, question.id, {
              question,
              answer,
            })
            this.$refs.programForm.setAnswerForSingleQuestion({
              questionId: question.id,
              answer,
            })
          }
        }
      })
    },

    updateFaq() {
      const faqFields = (this.formData || [])
        .filter((field) => /^faq_/.test(field.question.profile_attr))
        .filter((field) => field.value)
      faqFields.forEach((field) => {
        const faqElement = getStartupFaqElement({
          items: this.startup.faq[0].items,
          questionText: field.question.text,
        })
        if (!faqElement) {
          return
        }
        const { item, idx } = faqElement
        this.$store.commit('startupProfile/UPDATE_FAQ_ANSWER', {
          elementId: idx,
          sectionId: 0,
          value: field.value,
        })
      })
    },
  },
}

function getStartupFaqElement({ items, questionText }) {
  const idx = items.map((item) => item.question).indexOf(questionText)
  if (idx >= 0) {
    return { idx, item: items[idx] }
  } else {
    return null
  }
}

const EMPTY_ANSWER = { value: null, selected: [], data: {} }

function getAnswerFromProfile({ startup, attr, question }) {
  if (/^faq_/.test(attr)) {
    const questionText = question.text
    const answerHtml = getQuestionAnswerFromFaq({ startup, questionText })
    const text = htmlToText(answerHtml)
    return { ...EMPTY_ANSWER, value: text }
  } else {
    let value = startup[attr]
    let selected = []
    const data = {}
    if (_.isNumber(value)) {
      // do nothing
    } else if (_.isArray(value)) {
      selected = value
      value = null
    } else {
      value = htmlToText(value)
    }
    if (value || selected.length) {
      return { value, selected, data }
    }
  }
}

function getQuestionAnswerFromFaq({ startup, questionText }) {
  console.log(' => getQuestionAnswerFromFaq | ', questionText)
  for (const section of startup.faq) {
    for (const item of section.items) {
      if (htmlToText(item.question) === questionText) {
        return item.answer
      }
    }
  }
  if (toLegacyQuestionMap[questionText]) {
    console.log('   => mapped from legacy to:', questionText)
    questionText = toLegacyQuestionMap[questionText]
    return getQuestionAnswerFromFaq({ startup, questionText })
  }
  return null
}

function isFieldAnswerEmpty(answer) {
  answer = answer || {}
  return (
    !answer.value &&
    (answer.selected || []).length === 0 &&
    !(answer.data && answer.data.url)
  )
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.program-form {
  .el-progress {
    .el-progress__text {
      font-size: 14px;
    }
  }

  .table-completion-report {
    tbody tr td {
      font-size: 14px;
      padding-top: 6px;
      padding-bottom: 6px;
    }
  }

  .validation-text.text-danger {
    a {
      color: $bright;
    }
  }
}
</style>
